import type { ButtonHTMLAttributes, ReactNode } from 'react';
import React from 'react';

import type { PolymorphicComponentProps } from '../../utilities/types/polymorphicAsProp';
import type { BadgeProps } from '../Badge/Badge';
import { Icon } from '../../assets/Icon/Icon';
import { fade, palette } from '../../common/colors';
import { selectors } from '../../common/selectors';
import { colors, darkThemeSelector, shadows, styled } from '../../stitches.config';
import { Small } from '../../text/Small';
import { AlignStack } from '../../utilities/AlignStack/AlignStack';
import { Badge, BadgeIcon } from '../Badge/Badge';

const PromoDismissIcon = styled(Icon, {
  color: '$$iconColor',

  '@notDesktop': {
    width: '$12',
    height: '$12',
  },

  '@desktop': {
    width: '$10',
    height: '$10',
  },
});
PromoDismissIcon.displayName = 'PromoDismissIcon';

const PromoDismissContainer = styled('button', {
  position: 'absolute',
  zIndex: 10,
  top: '$4',
  right: '$4',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: fade(palette.bgBrandLight, 0.7),
  strokeAll: colors.transparent,

  $$iconColor: colors.iconBrandLight,

  [darkThemeSelector]: {
    backgroundColor: fade(palette.bgBrandDark, 0.7),
    strokeAll: colors.transparent,

    $$iconColor: colors.iconBrandDark,
  },

  [selectors.hover]: {
    backgroundColor: fade(palette.bgBrandLight, 0.9),
    strokeAll: colors.strokeBrandLight,

    $$iconColor: colors.bodyBrandLight,

    [darkThemeSelector]: {
      backgroundColor: fade(palette.bgBrandDark, 0.9),
      strokeAll: colors.strokeBrandDark,

      $$iconColor: colors.bodyBrandDark,
    },
  },

  [selectors.focus]: {
    outline: 'none',
    backgroundColor: fade(palette.bgBrandLight, 1),
    boxShadow: shadows.focusRingLight,

    [darkThemeSelector]: {
      backgroundColor: fade(palette.bgBrandDark, 1),
      boxShadow: shadows.focusRingDark,
    },
  },

  '@notDesktop': {
    width: '$28',
    height: '$28',
    borderRadius: '$6',
  },

  '@desktop': {
    width: '$20',
    height: '$20',
    borderRadius: '$4',
  },
});
PromoDismissContainer.displayName = 'PromoDismissContainer';

export interface PromoDismissProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  onClick?: (event: any) => void;
  ref?: React.Ref<HTMLButtonElement | null>;
}

export function PromoDismiss({ ref, ...props }: PromoDismissProps) {
  return (
    <PromoDismissContainer aria-label="Dismiss" type="button" tabIndex={0} {...props} ref={ref}>
      <PromoDismissIcon icon="cross" />
    </PromoDismissContainer>
  );
}

const PromoBanner = styled('img', {
  minHeight: '$80',
  backgroundColor: colors.bgNeutralLight,
  maxWidth: '100%',
  objectFit: 'cover',
  border: 'none',
  borderRadius: '$8 $8 0 0',
  boxShadow: 'none',

  [darkThemeSelector]: {
    backgroundColor: colors.bgNeutralDark,
  },
});
PromoBanner.displayName = 'PromoBanner';

const PromoBadge = styled(Badge, {
  marginTop: '-$16',
});
PromoBadge.displayName = 'PromoBadge';

const PromoHeading = styled(Small, {
  display: 'flex',
  fontWeight: '$bold',
});
PromoHeading.displayName = 'PromoHeading';

const PromoBody = styled(Small, {
  flexGrow: 1,
  display: 'flex',
});
PromoBody.displayName = 'PromoBody';

const PromoActions = styled('div', {
  display: 'flex',
  marginTop: '$12',
});
PromoActions.displayName = 'PromoActions';

const PromoDetails = styled('div', {
  padding: '$8 $12',
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  gap: '$4',
});
PromoDetails.displayName = 'PromoDetails';

const PromoCard = styled('div', {
  position: 'relative',
  zIndex: 0,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  width: '100%',
  background: colors.bgApplicationLight,
  boxShadow: shadows.overlayLight,
  borderRadius: '$8',
  transition: 'transform 150ms ease-out',

  [darkThemeSelector]: {
    background: colors.bgApplicationDark,
    boxShadow: shadows.overlayDark,
  },

  '&::before': {
    // Overwrites the forced focus ring from `Link`.
    display: 'none !important',
  },

  [selectors.focus]: {
    outline: 'none',
    boxShadow: shadows.focusRingLight,

    [darkThemeSelector]: {
      boxShadow: shadows.focusRingDark,
    },
  },
});
PromoCard.displayName = 'PromoCard';

const PromoContainer = styled('div', {
  display: 'grid',

  [`&:hover > ${PromoCard}`]: {
    transform: 'translateY(-2px)',
  },
});
PromoContainer.displayName = 'PromoContainer';

type PromoProps = {
  badge?: BadgeProps;
  banner?: string | ReactNode;
  dismiss?: () => void;
  heading?: string;
  body?: string;
  actions?: ReactNode;
};

export function Promo<Tag extends React.ElementType>({
  badge,
  banner,
  dismiss,
  heading,
  body,
  actions,
  ...remaining
}: PolymorphicComponentProps<Tag, PromoProps>) {
  return (
    <PromoContainer>
      <PromoCard tabIndex={0} {...remaining}>
        {banner && (typeof banner === 'string' ? <PromoBanner src={banner} /> : banner)}
        <PromoDetails>
          {banner ? (
            <>
              {badge && (
                <PromoBadge
                  arrangement="leading-icon"
                  icon="star"
                  size="small"
                  variant="brand"
                  {...badge}
                />
              )}
              {heading && <PromoHeading>{heading}</PromoHeading>}
            </>
          ) : (
            <AlignStack
              gap={4}
              align="center"
              start={
                badge ? (
                  <BadgeIcon
                    arrangement="leading-icon"
                    icon="star"
                    size="small"
                    variant="brand"
                    {...badge}
                  />
                ) : undefined
              }
            >
              {heading && <PromoHeading>{heading}</PromoHeading>}
            </AlignStack>
          )}

          {body && <PromoBody>{body}</PromoBody>}
          {actions && <PromoActions>{actions}</PromoActions>}
        </PromoDetails>
        {dismiss && <PromoDismiss onClick={dismiss} />}
      </PromoCard>
    </PromoContainer>
  );
}
